import React from "react";
import { withNamespaces } from "react-i18next";
import DefaultAvatar from "src/assets/images/users/def.png";
import { Card, CardBody, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";
import DateUtils from "src/services/utils/DateUtils";
import { STANDARD_DATE_FORMAT } from "src/components/constants";
import { TasksStatuses } from "src/modules/data-protection/constants";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants/Common"
import {
    HiChevronDoubleDown,
    HiChevronDoubleUp,
    HiMenuAlt4,
} from "react-icons/hi";
import { handleAddEllipsis } from "src/utils/utils";

const TasksPriorities = {
    low: {
        title: "Low",
        color: "#1CBB8C",
        icon: <HiChevronDoubleDown />,
    },
    medium: {
        title: "Medium",
        color: "#FCC34B",
        icon: <HiMenuAlt4 />,
    },
    high: {
        title: "High",
        color: "#DC3545",
        icon: <HiChevronDoubleUp />,
    },
};

const TaskCardItem = ({ t, data }) => {
    const dateUtils = new DateUtils();

    const renderTaskStatus = () => {
        const statusData = TasksStatuses[data.status];

        return (
            <span
                className="me-1 dt-task-status text-dark"
                style={{
                    backgroundColor: `${statusData?.color}`,
                }}
            >
                {t(statusData?.title)}
            </span>
        );
    };

    const renderTaskPriority = () => {
        const priorityData = TasksPriorities[data.priority];

        return (
            <span
                className="me-1 dt-task-status text-capitalize text-dark"
                style={{
                    backgroundColor: `${priorityData?.color}`,
                }}
            >
                <span>{priorityData?.icon}</span>
                {t(priorityData?.title)}
            </span>
        );
    };

    const renderSubTitles = (data) => {
        if (data?.questionnaire === "data_subject_request") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('Request ID')}: ${data?.caseSecondaryId}`}
                    </p>
                </div>
            )
        }
        else if (data?.questionnaire === "data_breach_incident") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('Incident ID')}: ${data?.caseSecondaryId}`}
                    </p>
                </div>
            )
        }
        else if (data?.questionnaire === "software") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('Software ID')}: ${data?.asset_id}`}
                    </p>
                    <p className="mb-1">
                        {t("Service")}: {handleAddEllipsis(data.reportCase?.software?.service, 50)}
                    </p>
                </div>
            )
        }
        else if (data?.questionnaire === "tom") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('TOM ID')}: ${data?.reportCase?.tom?.secondary_id ?? data?.caseSecondaryId}`}
                    </p>
                    <p className="mb-1">
                        {t("TOM Name")}: {handleAddEllipsis(data.reportCase?.tom?.name, 50)}
                    </p>
                </div>
            )
        }
        else if (data?.questionnaire === "control") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('Control ID')}: ${data.reportCase?.control?.id}`}
                    </p>
                    <p className="mb-1">
                        {t("Control Title")}: {handleAddEllipsis(data.reportCase?.control?.title, 50)}
                    </p>
                </div>
            )
        }
        else if (data?.questionnaire === "other_asset") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('Other Asset ID')}: ${data.reportCase?.otherAsset?.secondary_id}`}
                    </p>
                    <p className="mb-1">
                        {t("Other Asset Name")}: {handleAddEllipsis(data.reportCase?.otherAsset?.name, 50)}
                    </p>
                </div>
            )
        }
        else if (data?.questionnaire === "general") {
            return (
                <div
                    className="mb-4"
                    style={{
                        fontSize: 12,
                        borderLeft: "4px solid #eee",
                        paddingLeft: 20,
                        color: "#999",
                    }}
                >
                    <p className="mb-1">
                        {`${t('Risk ID')}: ${data.risk_secondary_id}`}
                    </p>
                </div>
            )
        }

        return null;
    };

    let linkTo = '';
    let subTitle = '';

    if (data?.questionnaire === "data_subject_request") {
        linkTo = `/admin/data-protection/data-subject-request/details/${data?.reportCase?.caseId}?tab=task-management&taskId=${data?.id}`;
    }
    else if (data?.questionnaire === "data_breach_incident") {
        linkTo = `/admin/data-protection/incident-register/details/${data?.reportCase?.caseId}?tab=task-management&taskId=${data?.id}`;
    }
    else if (data?.questionnaire === "software") {
        linkTo = `/admin/data-protection/softwares/${data?.reportCase?.software?.id}?tab=task-management&taskId=${data?.id}`;
    }
    else if (data?.questionnaire === "tom") {
        const tomId = data?.reportCase?.tom?.id ?? data?.asset_id;
        linkTo = `/admin/data-protection/toms/${tomId}/details?tab=task-management&id=${data?.id}`;
    }
    else if (data?.questionnaire === "control") {
        linkTo = `/admin/data-protection/tasks/${data?.id}/details`;
    }
    else if (data?.questionnaire === "other_asset") {
        linkTo = `/admin/data-protection/other-assets/${data?.reportCase?.otherAsset?.id}?tab=task-management&taskId=${data?.id}`;
    }
    else if (data?.questionnaire === "general") {
        linkTo = `/admin/risk-management/task/${data?.id}/details?incomming_tab=mitigation-tasks`;
    }
    else {
        linkTo = `/admin/report/task/${data["id"]}`;
    }

    return (
        <React.Fragment>
            <Card className="task-box" >
                <div class="progress-container">
                    <div class="progress-bar"></div>
                </div>
                <CardBody className="borad-width">
                    <div className="float-end ms-2">
                        <span>
                            {`${t("Deadline")}: ${dateUtils.convertTimeStampToDate(
                                data["endDate"],
                                STANDARD_DATE_FORMAT
                            )}`}
                        </span>
                    </div>

                    <div className="mb-3">
                        <Link to={linkTo}>
                            <span>#{formatTaskAndRiskId(
                                'task',
                                (data?.questionnaire === "software" || data?.questionnaire === "other_asset") ? data.asset_id : data?.caseSecondaryId,
                                data?.risk_secondary_id ?? data?.secondaryId,
                                data?.is_mitigation,
                                data.questionnaire,
                                data?.task_secondary_id ?? data?.secondaryId
                            )}</span>
                        </Link>
                    </div>

                    <div>
                        <h5 className="mb-4">
                            <Link to={linkTo}>
                                <span className="text-dark">
                                    {data.isRecurring && data.recurrenceSecondaryId && (
                                        <i
                                            className="ri-repeat-2-line text-info me-1 font-size-18 align-middle"
                                        />
                                    )}
                                    {data.title}{data.isRecurring && data.recurrenceSecondaryId ? `-${data.recurrenceSecondaryId}` : ''}
                                </span>
                            </Link>
                        </h5>

                        {renderSubTitles(data)}
                    </div>

                    <div className="mb-1">
                        <div className="flex justify-content-between">
                            <div className="d-inline-flex align-items-center mb-0 w-50">
                                <span className="me-2">{`${t("Team")}: `}</span>

                                {data["team"].map((member, key) => (
                                    <div className="team-member me-1" key={key}>
                                        <Link
                                            to="#"
                                            className="team-member d-inline-block"
                                            id={`owner-${member.id}`}
                                        >
                                            <div className="avatar-xs">
                                                <span
                                                    className="avatar-title bg-soft-primary text-primary rounded-circle text-capitalize"
                                                    style={{ overflow: "hidden" }}
                                                >
                                                    <img
                                                        alt="member's avatar"
                                                        className="w-100 h-100 d-block"
                                                        src={member.avatar ? member.avatar : DefaultAvatar}
                                                        style={{
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                </span>
                                            </div>
                                        </Link>
                                        <UncontrolledTooltip
                                            target={`owner-${member.id}`}
                                            placement="top"
                                        >
                                            <div className="text-capitalize text-left">
                                                {t(member.type)}:
                                                <br />
                                                {member.name}
                                            </div>
                                        </UncontrolledTooltip>
                                    </div>
                                ))}
                            </div>

                            <div className="d-inline-flex mb-0 text-right flex-row-reverse w-50">
                                {data.status && <>{renderTaskStatus()}</>}

                                {data.priority && <>{renderTaskPriority()}</>}
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </React.Fragment>
    );
};

export default withNamespaces()(TaskCardItem);
